import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { fireGenericBrowserNotification } from '@/lib/messaging/in-app-notifications';
import { isSchedulingOnly } from '@/lib/mobile-mode';

type NotificationType = 'success' | 'error' | 'info' | 'warning';

interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
  duration?: number;
  timestamp: Date;
}

interface NotificationContextType {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const addNotification = (notification: Omit<Notification, 'id' | 'timestamp'>) => {
    const newNotification: Notification = {
      ...notification,
      id: crypto.randomUUID(),
      timestamp: new Date(),
      duration: notification.duration || 5000
    };

    setNotifications(prev => [...prev, newNotification]);

    // Auto-remove after duration (skip if duration is 0 for persistent notifications)
    if (newNotification.duration > 0) {
      setTimeout(() => {
        removeNotification(newNotification.id);
      }, newNotification.duration);
    }
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  // CRITICAL FIX #3: Memoize context value to prevent unnecessary re-renders
  const value = React.useMemo(() => ({
    notifications,
    addNotification,
    removeNotification,
    clearAll
  }), [notifications]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationContainer />
    </NotificationContext.Provider>
  );
}

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

// Notification display component
function NotificationContainer() {
  const { notifications, removeNotification } = useNotifications();

  const icons = {
    success: CheckCircle,
    error: AlertCircle,
    info: Info,
    warning: AlertCircle
  };

  const colors = {
    success: 'text-success border-success/20 bg-success/10',
    error: 'text-destructive border-destructive/20 bg-destructive/10',
    info: 'text-primary border-primary/20 bg-primary/10',
    warning: 'text-warning border-warning/20 bg-warning/10'
  };

  const isPersistent = (notification: Notification) => notification.duration === 0;

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
      {notifications.map((notification) => {
        const Icon = icons[notification.type];
        
        return (
          <Card
            key={notification.id}
            className={cn(
              'p-4 shadow-lg animate-in slide-in-from-right duration-300',
              colors[notification.type],
              isPersistent(notification) && 'border-l-4 border-l-primary' // Visual indicator for persistent notifications
            )}
          >
            <div className="flex items-start space-x-3">
              <Icon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-medium">{notification.title}</p>
                {notification.message && (
                  <p className="text-sm mt-1 opacity-90">{notification.message}</p>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 -mr-2 -mt-1"
                onClick={() => removeNotification(notification.id)}
                title={isPersistent(notification) ? "Dismiss permanent notification" : "Close notification"}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// Hook for real-time notifications
export function useRealtimeNotifications() {
  const { addNotification } = useNotifications();
  const { profile, loginMethod } = useAuth();

  useEffect(() => {
    // Scheduling-only surface: the operational realtime toasts set up below
    // (new task assigned / task completed, urgent communications, Team Log
    // @mentions) are NOT scheduling content, so a scheduling-only user must
    // never get them — matching the notification bell, which already filters to
    // schedule types (src/lib/notification-surface.ts). Skip the operational
    // subscriptions entirely here.
    if (isSchedulingOnly(profile?.role, loginMethod)) {
      return;
    }

    // CRITICAL FIX #2: Block ALL real-time subscriptions during PIN sessions to prevent 401 errors
    const isPinSession = !!localStorage.getItem('pin_session');
    if (isPinSession) {
      console.log('🔔 [PIN_GUARD] Blocking real-time notification subscriptions during PIN session');
      console.log('🔔 [PIN_MODE] Starting polling for notifications instead');

      // Get PIN session data
      let pinSession;
      try {
        const savedSession = localStorage.getItem('pin_session');
        if (savedSession) {
          pinSession = JSON.parse(savedSession);
        }
      } catch (error) {
        console.error('Failed to parse PIN session for notifications:', error);
        return;
      }

      if (!pinSession?.profile?.id || !pinSession?.company_code) {
        console.log('🔔 [PIN_MODE] No valid PIN session for notifications');
        return;
      }

      // FIXED: Do NOT show toast notifications for PIN users
      // Notifications are handled by PINNotificationCenter component (bell icon)
      // The useNotificationsForPIN hook fetches notifications for display in the bell
      console.log('🔔 [PIN_MODE] Notifications will appear in bell icon only (no toasts)');
      return;
    }

    // Get current user profile - check PIN session first, then auth profile
    let currentProfile = profile;
    
    // Check for PIN session profile if no auth profile or if we want to prioritize PIN session
    if (!currentProfile) {
      try {
        const savedSession = localStorage.getItem('pin_session');
        if (savedSession) {
          const session = JSON.parse(savedSession);
          currentProfile = session.profile;
          console.log('🔔 Using PIN session profile for notifications:', {
            id: currentProfile?.id,
            name: `${currentProfile?.first_name} ${currentProfile?.last_name}`,
            company_id: currentProfile?.company_id
          });
        }
      } catch (error) {
        console.error('Failed to parse PIN session for notifications:', error);
      }
    } else {
      console.log('🔔 Using auth profile for notifications:', {
        id: currentProfile.id,
        name: `${currentProfile.first_name} ${currentProfile.last_name}`,
        company_id: currentProfile.company_id
      });
    }

    if (!currentProfile?.company_id || !currentProfile?.id) {
      console.log('🔔 No valid profile found for real-time notifications');
      return;
    }

    console.log('🔔 Setting up real-time notifications for profile:', currentProfile.id);

    // IMPORTANT: mentions get their OWN channel. Supabase Realtime
    // silently drops every postgres_changes binding on a channel when
    // ANY of its tables is missing from the supabase_realtime
    // publication (the join still reports SUBSCRIBED). tasks and
    // communications are not published, so bundling mentions with them
    // killed mention delivery too — that's why "you were tagged" never
    // fired until the 2026-06-09 fix published mentions and split this
    // channel. Keep tasks/communications isolated below so they can't
    // poison mentions again; they stay dormant until those tables are
    // added to the publication.
    const mentionsChannel = supabase
      .channel(`notifications-mentions-${currentProfile.company_id}-${currentProfile.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'mentions',
        filter: `mentioned_user_id=eq.${currentProfile.id}`
      }, (payload) => {
        console.log('Mention notification received:', payload);
        // NOTE: the in-app toast is intentionally NOT fired here. Every
        // mention also inserts a `mention` row into `notifications`, which
        // NotificationCenter's realtime channel already surfaces as a toast
        // (with a "View" action). Firing our own toast too showed the user
        // two toasts for a single @mention. This channel now only owns the
        // OS/browser pop-up, which fires even when the tab is backgrounded
        // (in-app toasts are invisible there — issue reports 2026-06-09).
        if (payload.new.content_type === 'task_assignment') {
          fireGenericBrowserNotification({
            title: 'New task assignment',
            body: 'You have been assigned a new task in DashAlign',
            tag: `mention:${payload.new.id}`,
          });
        } else {
          fireGenericBrowserNotification({
            title: 'You were tagged in DashAlign',
            body: `Someone mentioned you in a ${String(payload.new.content_type ?? 'post').replace(/_/g, ' ')}`,
            tag: `mention:${payload.new.id}`,
          });
        }
      })
      .subscribe((status) => {
        console.log('Real-time mention subscription status:', status);
      });

    const channel = supabase
      .channel(`notifications-${currentProfile.company_id}-${currentProfile.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'tasks',
        filter: `assigned_to=eq.${currentProfile.id}`
      }, (payload) => {
        console.log('Task INSERT notification received:', payload);
        addNotification({
          type: 'info',
          title: 'New Task Assigned',
          message: `You have a new task: ${payload.new.title}`,
          duration: 0 // Persist until manually dismissed
        });
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'tasks',
        filter: `assigned_to=eq.${currentProfile.id}`
      }, (payload) => {
        console.log('Task UPDATE notification received:', payload);
        if (payload.new.assigned_to === currentProfile.id && payload.old.assigned_to !== currentProfile.id) {
          addNotification({
            type: 'info',
            title: 'Task Assigned to You',
            message: `You have been assigned: ${payload.new.title}`,
            duration: 0 // Persist until manually dismissed
          });
        }
      })
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'communications'
      }, (payload) => {
        if (payload.new.priority === 'urgent') {
          addNotification({
            type: 'warning',
            title: 'Urgent Communication',
            message: payload.new.title
          });
        } else if (payload.new.category === 'staff-update' && payload.new.title === 'Task Completed') {
          addNotification({
            type: 'success',
            title: 'Task Completed',
            message: payload.new.message
          });
        }
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'tasks',
        filter: `created_by=eq.${currentProfile.id}`
      }, (payload) => {
        if (payload.new.status === 'completed' && payload.old.status !== 'completed') {
          addNotification({
            type: 'success',
            title: 'Task Completed',
            message: `Your task "${payload.new.title}" has been completed`
          });
        }
      })
      .subscribe((status) => {
        console.log('Real-time subscription status:', status);
      });

    return () => {
      console.log('🔔 Cleaning up real-time notifications channels');
      supabase.removeChannel(mentionsChannel);
      supabase.removeChannel(channel);
    };
  }, [profile, loginMethod, addNotification]);

  // Listen for PIN session changes to restart notifications
  useEffect(() => {
    const handlePinSessionUpdate = () => {
      // Force re-run of the main effect by updating a dependency
      // The main effect will pick up the new PIN session profile
    };

    window.addEventListener('pin_session_updated', handlePinSessionUpdate);
    return () => {
      window.removeEventListener('pin_session_updated', handlePinSessionUpdate);
    };
  }, []);
  
  // Enhanced task completion notifications
  useEffect(() => {
    // Scheduling-only surface: "Task Completed" is operational, not scheduling —
    // withhold it (matches the bell + the main effect above).
    if (isSchedulingOnly(profile?.role, loginMethod)) return;

    // CRITICAL FIX #2: Block ALL real-time subscriptions during PIN sessions
    const isPinSession = !!localStorage.getItem('pin_session');
    if (isPinSession) {
      console.log('🔔 [PIN_GUARD] Blocking task completion subscriptions during PIN session');
      return;
    }

    if (!profile?.id) return;
    
    const channel = supabase
      .channel(`task-completion-${profile.id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'tasks',
        filter: `created_by=eq.${profile.id}`
      }, (payload) => {
        // Check if task was completed by someone else
        if (payload.new.status === 'completed' && 
            payload.old.status !== 'completed' && 
            payload.new.completed_by !== profile.id) {
          addNotification({
            type: 'success',
            title: 'Task Completed',
            message: `Your assigned task "${payload.new.title}" has been completed`,
            duration: 0 // Persist until manually dismissed
          });
        }
      })
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile?.id, profile?.role, loginMethod, addNotification]);
}